import DceHTMLElement from "dce-engine/lib/types/DceHTMLElement";

export interface DomCommand<K extends keyof DomCommandDataMap> {
    command: K;
    data: DomCommandDataMap[K];
}

export type DomCommandDataMap = {
    replace: RHtmlCommandData;
    append: AHtmlCommandData;
    prepend: PHtmlCommandData;
    innerhtml: IHtmlCommandData,
    setattr: SetAttrCommandData,
    setprop: SetPropCommandData,
    setvalue: SetValueCommandData,
    addclass: AddClassCommandData,
    removeclass: RemoveClassCommandData,
    setstyle: SetStyleCommandData,
    compcall: ComponentCallCommandData
    setselectoptions: SetSelectOptions
}

//region data

interface RHtmlCommandData {
    rhtml: string;
}

interface AHtmlCommandData {
    ahtml: string;
}

interface PHtmlCommandData {
    phtml: string;
}

interface IHtmlCommandData {
    ihtml: string;
}

/**
 * setattr
 * Ustawienie atrybutu na elmencie
 */
interface SetAttrCommandData {
    /**
     * Atrybut
     */
    a: string;

    /**
     * Wartość
     * null - usunięcie atrybutu
     */
    av: string;
}


/**
 * setprop
 * Ustawienie atrybutu na elmencie
 */
interface SetPropCommandData {
    /**
     * Property
     */
    p: string;

    /**
     *
     */
    pv: boolean;
}


/**
 * setvalue
 * Ustawienie wartości na inpucie
 */
interface SetValueCommandData {
    /**
     * Value
     */
    val: string;
}


/**
 * addclass
 * Ustawienie atrybutu na elmencie
 */
interface AddClassCommandData {
    /**
     * Value
     */
    add: string[];
}


/**
 * removeclass
 * Ustawienie atrybutu na elmencie
 */
interface RemoveClassCommandData {
    /**
     * Value
     */
    rem: string[];
}

/**
 * setstyle
 * Ustawienie atrybutu na elmencie
 */
interface SetStyleCommandData {
    /**
     * Value
     */
    styles: Partial<CSSStyleDeclaration>;
}

/**
 * setstyle
 * Ustawienie atrybutu na elmencie
 */
interface ComponentCallCommandData {
    /**
     * Component
     */
    comp: string;

    /**
     * Function
     */
    method: string;

    /**
     * f attribute
     */
    attr: any;
}

interface SetSelectOptions {
    //lista opcji jaki [ [value, label], ....]
    options: string[][]
}

// endregion

type Call<X extends keyof DomCommandDataMap> = ((e: HTMLElement, data: DomCommandDataMap[X]) => void);

type DomCallbacks = {
    [X in keyof DomCommandDataMap]: Call<X>;
};


export class DomCommands {

    private static cls: DomCallbacks = {

        setselectoptions(e:HTMLElement, data: DomCommandDataMap["setselectoptions"]): void {
            if(e.tagName.toLowerCase() != 'select'){
                return;
            }

            let options = data.options.map(opt => {
                let option = document.createElement('option');
                option.innerHTML = opt[1];
                option.value = opt[0];

                return option;
            })

            e.replaceChildren(...options);

        },

        replace(e: HTMLElement, data: DomCommandDataMap["replace"]): void {
            let tmp = document.createElement('div');
            tmp.innerHTML = data.rhtml.trim();

            if(null == tmp.firstElementChild){
                e.replaceWith(document.createTextNode(""))
                return;
            }

            e.replaceWith(tmp.firstElementChild);
        },

        append(e: HTMLElement, data: DomCommandDataMap["append"]): void {
            e.insertAdjacentHTML("beforeend", data.ahtml);
        },

        prepend(e: HTMLElement, data: DomCommandDataMap["prepend"]): void {
            e.insertAdjacentHTML("afterbegin", data.phtml);
        },

        addclass(e: HTMLElement, data: DomCommandDataMap["addclass"]): void {
            if (data.add) {
                e.classList.add(...data.add);
            }
        },

        compcall(e: HTMLElement, data: DomCommandDataMap["compcall"]): void {

            let dceElem = (e as DceHTMLElement).ext;
            if (dceElem) {
                let comp = dceElem.getComponent(data.comp);

                if(!comp){
                    console.warn("Component " + data.comp + " not found on ", e);
                    return;
                }

                let xxx: {[index: string]:any} = comp as unknown;

                let method = xxx[data.method];

                if(method){
                    method.call(comp, data.attr);
                }
            }
        },

        innerhtml(e: HTMLElement, data: DomCommandDataMap["innerhtml"]): void {
            e.innerHTML = data.ihtml;
        },

        removeclass(e: HTMLElement, data: DomCommandDataMap["removeclass"]): void {
            if (data.rem) {
                e.classList.remove(...data.rem);
            }
        },

        setattr(e: HTMLElement, data: DomCommandDataMap["setattr"]): void {

            if (!data.a) {
                return;
            }

            if (null == data.av) {
                if (e.hasAttribute(data.a)) {
                    e.removeAttribute(data.a)
                }
                return;
            }

            if (e.getAttribute(data.a) != data.av) {
                e.setAttribute(data.a, data.av);
            }

        },

        setprop(e: HTMLElement, data: DomCommandDataMap["setprop"]): void {
            if (!data.p) {
                return;
            }

            if(data.p == 'checked'){

                if(e instanceof HTMLInputElement){
                    if(e.checked != data.pv){
                        e.checked = data.pv;
                    }
                }

                return;
            }

            if (data.pv && e.getAttribute(data.p) != data.p) {
                e.setAttribute(data.p, data.p);
            } else {
                if (e.hasAttribute(data.p)) {
                    e.removeAttribute(data.p);
                }
            }
        },

        setstyle(e: HTMLElement, data: DomCommandDataMap["setstyle"]): void {
            for (let k in data.styles) {
                e.style[k] = data.styles[k];
            }
        },

        setvalue(e: HTMLElement, data: DomCommandDataMap["setvalue"]): void {
            if (e instanceof HTMLInputElement) {
                (e as HTMLInputElement).value = data.val;
            }

            if(e instanceof HTMLSelectElement){

            }
        }

    }

    public static callz<K extends keyof DomCommandDataMap>(e: HTMLElement, command: K, x: DomCommandDataMap[K]): void {
        (this.cls[command] as Call<K>)(e, x);
    }

    public static execute<K extends keyof DomCommandDataMap>(e: HTMLElement, selector: string, command: K, data: DomCommandDataMap[K]) {

        try {
            console.log("EXEC: ", selector);

            if(selector === '.'){
                DomCommands.callz(e, command, data)
                return;
            }

            e.querySelectorAll(selector).forEach((elem) => {
                console.log("  E:", elem);
                if (elem instanceof HTMLElement) {
                    DomCommands.callz(elem, command, data);
                }
            });
        }catch (e){
            console.error(e);
        }

    }

}
